import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Calculator, CheckCircle, ArrowRight, ArrowLeft, Lock, Activity, PhoneCall, Scale, Loader2 } from 'lucide-react';
import DataProtectionCheckbox from '../components/DataProtectionCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const ProtocoloPage = () => {
  const [gateUnlocked, setGateUnlocked] = useState(false);
  const [gateForm, setGateForm] = useState({ nombre: '', email: '', perfil: '' });
  const [gatePrivacy, setGatePrivacy] = useState(false);
  const [gateLoading, setGateLoading] = useState(false);
  const [gateError, setGateError] = useState('');

  const [paso, setPaso] = useState('inicio');
  const [datos, setDatos] = useState({
    numTrabajadores: '10',
    salarioBrutoMensual: '2000',
    antiguedadAnios: '5',
    costeAdaptacion: '18000',
    duracionBajaMeses: 7,
    tieneInformeDetallado: false,
    modalidadPreventiva: 'SPA'
  });

  const handleNumericChange = (field, value) => {
    const cleaned = value.replace(/[^0-9]/g, '');
    setDatos(prev => ({ ...prev, [field]: cleaned }));
  };

  const num = (val) => Number(val) || 0;

  const [lead, setLead] = useState({ nombre: '', email: '', empresa: '', preocupacion: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateLoading(true);
    setGateError('');
    try {
      await axios.post(`${API_URL}/api/leads`, {
        nombre: gateForm.nombre,
        email: gateForm.email,
        telefono: '',
        cargo: gateForm.perfil,
        empresa: '',
        mensaje: `Acceso Protocolo Reincorporación | Perfil: ${gateForm.perfil}`,
        origen: 'protocolo_gate'
      });
      setGateUnlocked(true);
      setLead(prev => ({ ...prev, nombre: gateForm.nombre, email: gateForm.email }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (!err.response) {
        setGateError('Error de conexión. Compruebe su conexión a internet.');
      } else if (status === 500) {
        setGateError('Error del servidor. Inténtelo de nuevo en unos minutos.');
      } else {
        setGateError(typeof detail === 'string' ? detail : 'Error al procesar. Inténtelo de nuevo.');
      }
    } finally {
      setGateLoading(false);
    }
  };

  const calculos = useMemo(() => {
    const seisMensualidades = num(datos.salarioBrutoMensual) * 6;
    const salarioDiario = (num(datos.salarioBrutoMensual) * 12) / 365;
    const indemnizacionImprocedente = salarioDiario * 33 * num(datos.antiguedadAnios);
    const limiteCargaExcesiva = Math.max(seisMensualidades, indemnizacionImprocedente);
    const esCargaExcesiva = num(datos.costeAdaptacion) > limiteCargaExcesiva && num(datos.numTrabajadores) < 25;

    const riesgoBajaLarga = datos.duracionBajaMeses >= 6; 
    const riesgoInforme = !datos.tieneInformeDetallado; 
    
    let nivelRiesgo = "Bajo";
    if (riesgoBajaLarga || riesgoInforme) nivelRiesgo = "Alto";
    if (riesgoBajaLarga && riesgoInforme) nivelRiesgo = "Crítico";

    return { limiteCargaExcesiva, esCargaExcesiva, riesgoBajaLarga, riesgoInforme, nivelRiesgo };
  }, [datos]);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/leads`, {
        nombre: lead.nombre,
        email: lead.email,
        empresa: lead.empresa,
        cargo: 'Protocolo Retorno',
        mensaje: lead.preocupacion || `Diagnóstico: ${calculos.nivelRiesgo} | Trabajadores: ${datos.numTrabajadores} | Baja: ${datos.duracionBajaMeses} meses`,
        origen: 'protocolo_retorno'
      });
      setPaso('finalizado');
    } catch (err) {
      setError('Error al enviar. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-blue-100" data-testid="protocolo-page">
      {/* Barra de seguridad LOPDGDD */}
      <div className="bg-slate-900 text-slate-400 py-2 px-6 text-[10px] flex justify-between items-center uppercase tracking-widest">
        <span className="flex items-center gap-2"><Lock size={10} /> Entorno de datos anonimizado bajo LOPDGDD</span>
        <span>Servidor: España-Madrid-01</span>
      </div>

      {/* Header con navegación */}
      <div className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition" data-testid="back-to-home">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Volver a inicio</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/m7tg5pgh_logo.jpg" 
              alt="PrevenciónLegal @tecnoabogado" 
              className="h-8 w-auto"
            />
          </Link>
        </div>
      </div>

      {paso !== 'finalizado' && (
        <header className="bg-white border-b border-slate-200 py-16 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-block px-4 py-1.5 bg-rose-50 text-rose-700 text-xs font-bold mb-6 border border-rose-100 uppercase tracking-wide">
              Actualización legal: marzo de 2026
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
              Reincorporaciones tras baja larga: los 7 errores que más dinero cuestan
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
              Evite sanciones de hasta 983.736 €. El Tribunal Supremo ya no acepta informes genéricos para despidos por ineptitud.
            </p>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto py-12 px-6">
        {/* Lead gate — preview + form before accessing full wizard */}
        {!gateUnlocked && paso !== 'finalizado' && (
          <div data-testid="protocolo-gate">
            {/* Preview cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
              {[
                { label: 'Sanciones LISOS', value: '983.736 €', desc: 'Máxima por infracción muy grave' },
                { label: 'Plazo trabajador/a', value: '10 días', desc: 'Para comunicar voluntad de continuar' },
                { label: 'Plazo empresa', value: '3 meses', desc: 'Para evaluar y resolver protocolo' },
                { label: 'Despido nulo', value: '100%', desc: 'Extinción sin protocolo = nulidad' },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-slate-200 p-4">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">{s.label}</p>
                  <p className="text-2xl font-black text-slate-900 mt-1">{s.value}</p>
                  <p className="text-xs text-rose-600 font-bold">{s.desc}</p>
                </div>
              ))}
            </div>

            {/* Gate form */}
            <div className="bg-white border border-slate-200 p-8 max-w-lg mx-auto" data-testid="protocolo-gate-form-container">
              <div className="text-center mb-6">
                <Calculator className="mx-auto text-rose-600 mb-3" size={32} />
                <h2 className="text-xl font-bold text-slate-900">Acceda al analizador de riesgos</h2>
                <p className="text-sm text-slate-500 mt-1">Calcule el nivel de riesgo legal de su empresa en 2 minutos</p>
              </div>
              <form onSubmit={handleGateSubmit} className="space-y-4" data-testid="protocolo-gate-form">
                {gateError && <p className="text-sm text-red-600 bg-red-50 p-3 border border-red-200">{gateError}</p>}
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Nombre *</label>
                  <input type="text" required value={gateForm.nombre}
                    onChange={(e) => setGateForm(f => ({ ...f, nombre: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                    data-testid="protocolo-gate-nombre" placeholder="Su nombre" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Email *</label>
                  <input type="email" required value={gateForm.email}
                    onChange={(e) => setGateForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                    data-testid="protocolo-gate-email" placeholder="Su email profesional" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Perfil *</label>
                  <select required value={gateForm.perfil}
                    onChange={(e) => setGateForm(f => ({ ...f, perfil: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm appearance-none"
                    data-testid="protocolo-gate-perfil">
                    <option value="">Seleccione su perfil</option>
                    <option value="Empresa / RRHH">Empresa / RRHH</option>
                    <option value="Servicio de Prevención (SPA/SPP)">Servicio de Prevención (SPA/SPP)</option>
                    <option value="Asesoramiento SPA">Asesoramiento SPA</option>
                    <option value="Trabajador/a">Trabajador/a</option>
                  </select>
                </div>
                <DataProtectionCheckbox checked={gatePrivacy} onChange={setGatePrivacy} id="protocolo-gate-privacy" />
                <button type="submit" disabled={gateLoading || !gatePrivacy}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-rose-600 text-white font-bold hover:bg-rose-700 transition disabled:opacity-50"
                  data-testid="protocolo-gate-submit">
                  {gateLoading ? <Loader2 className="animate-spin" size={20} /> : <>Acceder al analizador</>}
                </button>
              </form>
            </div>
          </div>
        )}

        {gateUnlocked && paso === 'inicio' && (
          <div className="text-center" data-testid="protocolo-start-section">
            <button
              onClick={() => setPaso('diagnostico')}
              className="bg-rose-600 text-white px-8 py-4 font-bold hover:bg-rose-700 transition-all shadow-lg flex items-center gap-2 mx-auto"
              data-testid="start-diagnostic-btn"
            >
              Iniciar autodiagnóstico legal <ArrowRight size={18} />
            </button>
          </div>
        )}

        {paso === 'diagnostico' && (
          <div className="grid lg:grid-cols-12 gap-12" data-testid="diagnostic-section">
            {/* Formulario Calculadora */}
            <div className="lg:col-span-7 bg-white p-8 border border-slate-200">
              <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                <Calculator className="text-rose-600" /> Parámetros de su empresa
              </h2>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Plantilla (nº trabajadores)</label>
                    <input 
                      type="text"
                      inputMode="numeric"
                      value={datos.numTrabajadores} 
                      onChange={(e) => handleNumericChange('numTrabajadores', e.target.value)} 
                      className="w-full p-4 bg-slate-50 border border-slate-200 outline-none focus:border-rose-500"
                      data-testid="input-trabajadores"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Coste de adaptación (€)</label>
                    <input 
                      type="text"
                      inputMode="numeric"
                      value={datos.costeAdaptacion} 
                      onChange={(e) => handleNumericChange('costeAdaptacion', e.target.value)} 
                      className="w-full p-4 bg-slate-50 border border-slate-200 outline-none focus:border-rose-500"
                      data-testid="input-coste"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Salario bruto mensual (€)</label>
                    <input 
                      type="text"
                      inputMode="numeric"
                      value={datos.salarioBrutoMensual} 
                      onChange={(e) => handleNumericChange('salarioBrutoMensual', e.target.value)} 
                      className="w-full p-4 bg-slate-50 border border-slate-200 outline-none focus:border-rose-500"
                      data-testid="input-salario"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Antigüedad (años)</label>
                    <input 
                      type="text"
                      inputMode="numeric"
                      value={datos.antiguedadAnios} 
                      onChange={(e) => handleNumericChange('antiguedadAnios', e.target.value)} 
                      className="w-full p-4 bg-slate-50 border border-slate-200 outline-none focus:border-rose-500"
                      data-testid="input-antiguedad"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Duración de la baja (meses): <span className="text-rose-600">{datos.duracionBajaMeses}</span></label>
                  <input 
                    type="range" 
                    min="1" 
                    max="24" 
                    value={datos.duracionBajaMeses} 
                    onChange={(e) => setDatos({...datos, duracionBajaMeses: Number(e.target.value)})} 
                    className="w-full h-2 bg-slate-200 appearance-none cursor-pointer accent-rose-600"
                    data-testid="slider-baja"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>1 mes</span>
                    <span>24 meses</span>
                  </div>
                </div>
                <div className="bg-rose-50 p-6 border border-rose-100">
                  <label className="flex items-start gap-4 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={datos.tieneInformeDetallado} 
                      onChange={(e) => setDatos({...datos, tieneInformeDetallado: e.target.checked})} 
                      className="mt-1 w-5 h-5 accent-rose-600"
                      data-testid="checkbox-informe"
                    />
                    <span className="text-sm text-slate-700 font-medium">¿El informe de aptitud detalla limitaciones funcionales concretas y su incidencia en el puesto?</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Dashboard Resultados */}
            <div className="lg:col-span-5 space-y-6">
              <div className={`p-8 border-2 ${calculos.nivelRiesgo === 'Crítico' ? 'bg-red-50 border-red-200' : calculos.nivelRiesgo === 'Alto' ? 'bg-amber-50 border-amber-200' : 'bg-slate-900 border-slate-800'}`} data-testid="risk-dashboard">
                <h3 className={`text-xl font-bold mb-6 flex items-center gap-2 ${calculos.nivelRiesgo === 'Crítico' ? 'text-red-800' : calculos.nivelRiesgo === 'Alto' ? 'text-amber-800' : 'text-white'}`}>
                  <Activity size={20} /> Riesgo preventivo actual
                </h3>
                <div className="space-y-4">
                  {num(datos.numTrabajadores) < 25 && (
                    <div className="bg-white p-5 border border-slate-200">
                      <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Límite carga excesiva (Ley 2/2025)</p>
                      <p className="text-slate-900 font-bold text-2xl mt-1">{calculos.limiteCargaExcesiva.toLocaleString('es-ES')} €</p>
                      <p className={`text-xs mt-2 font-bold ${calculos.esCargaExcesiva ? 'text-red-600' : 'text-green-600'}`} data-testid="carga-status">
                        {calculos.esCargaExcesiva ? '⚠️ Ajuste económicamente excesivo' : '✓ Adaptación legalmente exigible'}
                      </p>
                    </div>
                  )}
                  <div className="bg-white p-5 border border-slate-200">
                    <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Validez del informe (STS 123/2025)</p>
                    <p className={`font-bold mt-1 ${calculos.riesgoInforme ? 'text-red-600' : 'text-green-600'}`} data-testid="informe-status">
                      {calculos.riesgoInforme ? 'INSUFICIENTE (Riesgo Nulidad)' : 'Adecuado'}
                    </p>
                  </div>
                  <div className="bg-white p-5 border border-slate-200">
                    <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Nivel de riesgo global</p>
                    <p className={`font-bold text-xl mt-1 ${
                      calculos.nivelRiesgo === 'Crítico' ? 'text-red-600' : 
                      calculos.nivelRiesgo === 'Alto' ? 'text-amber-600' : 'text-green-600'
                    }`} data-testid="risk-level">
                      {calculos.nivelRiesgo}
                    </p>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setPaso('lead')} 
                className="w-full bg-slate-900 text-white p-6 font-bold text-lg flex items-center justify-between hover:bg-black transition shadow-xl"
                data-testid="get-report-btn"
              >
                Obtener informe completo <ArrowRight />
              </button>
            </div>
          </div>
        )}

        {paso === 'lead' && (
          <div className="max-w-6xl mx-auto" data-testid="lead-section">
            <h2 className="text-3xl font-black mb-4 text-center">Asegure su decisión empresarial</h2>
            <p className="text-slate-600 mb-8 text-center">Descargue su diagnóstico personalizado y el modelo de protocolo del Art. 25.3 de la LPRL.</p>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Vista previa del informe */}
              <div className="relative" data-testid="report-preview">
                <div className="bg-white border border-slate-200 shadow-lg overflow-hidden">
                  {/* Header del documento */}
                  <div className="bg-slate-900 text-white p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-slate-400">Informe confidencial</p>
                        <h3 className="font-bold text-lg">Diagnóstico de Riesgos LISOS</h3>
                        <p className="text-xs text-slate-400">Reincorporación tras Incapacidad Temporal</p>
                      </div>
                      <div className="text-right text-xs text-slate-400">
                        <p>Ref: PRL-2026-XXXX</p>
                        <p>Fecha: {new Date().toLocaleDateString('es-ES')}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contenido visible del informe */}
                  <div className="p-6 space-y-4 text-sm">
                    <div className="border-b border-slate-200 pb-4">
                      <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">1. Datos del caso</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <p><span className="text-slate-500">Plantilla:</span> <span className="font-medium">{datos.numTrabajadores || 0} trabajadores</span></p>
                        <p><span className="text-slate-500">Categoría:</span> <span className="font-medium">{num(datos.numTrabajadores) < 25 ? 'PYME sensible' : 'Empresa estándar'}</span></p>
                        <p><span className="text-slate-500">Duración IT:</span> <span className="font-medium">{datos.duracionBajaMeses} meses</span></p>
                        <p><span className="text-slate-500">Antigüedad:</span> <span className="font-medium">{datos.antiguedadAnios || 0} años</span></p>
                      </div>
                    </div>
                    
                    <div className="border-b border-slate-200 pb-4">
                      <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">2. Análisis de riesgo legal</h4>
                      <div className={`inline-block px-3 py-1 text-xs font-bold ${
                        calculos.nivelRiesgo === 'Crítico' ? 'bg-red-100 text-red-700' :
                        calculos.nivelRiesgo === 'Alto' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                      }`}>
                        NIVEL DE RIESGO: {calculos.nivelRiesgo.toUpperCase()}
                      </div>
                      <p className="mt-2 text-slate-600 text-xs leading-relaxed">
                        Conforme al Art. 25 LPRL y la doctrina establecida en STS 123/2025, el presente caso presenta 
                        {calculos.nivelRiesgo === 'Crítico' ? ' múltiples factores de riesgo que requieren actuación inmediata.' :
                         calculos.nivelRiesgo === 'Alto' ? ' factores de riesgo significativos que deben ser abordados.' :
                         ' un perfil de riesgo controlado, aunque se recomiendan medidas preventivas.'}
                      </p>
                    </div>

                    <div className="border-b border-slate-200 pb-4">
                      <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">3. Límite de carga excesiva (Ley 2/2025)</h4>
                      <p className="text-2xl font-bold text-slate-900">{calculos.limiteCargaExcesiva.toLocaleString('es-ES')} €</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Calculado según el máximo entre 6 mensualidades ({(num(datos.salarioBrutoMensual) * 6).toLocaleString('es-ES')} €) 
                        e indemnización por despido improcedente.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">4. Recomendaciones prioritarias</h4>
                      <ul className="space-y-1 text-xs text-slate-600">
                        <li className="flex items-start gap-2">
                          <span className="text-rose-600 font-bold">▸</span>
                          Solicitar al SPA informe detallado con limitaciones funcionales específicas
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-rose-600 font-bold">▸</span>
                          Documentar todas las adaptaciones de puesto consideradas
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-rose-600 font-bold">▸</span>
                          Verificar plazos: 10 días para comunicación inicial...
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Sección con blur - contenido oculto */}
                  <div className="relative">
                    <div className="p-6 space-y-4 text-sm blur-sm select-none" aria-hidden="true">
                      <div className="border-b border-slate-200 pb-4">
                        <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">5. Análisis jurisprudencial aplicable</h4>
                        <p className="text-xs text-slate-600">La STS 123/2025 de 15 de enero establece que los informes de aptitud genéricos...</p>
                        <p className="text-xs text-slate-600 mt-2">Asimismo, la STS 456/2024 determina que la carga de la prueba sobre la imposibilidad...</p>
                      </div>
                      <div className="border-b border-slate-200 pb-4">
                        <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">6. Protocolo de actuación paso a paso</h4>
                        <p className="text-xs text-slate-600">Fase 1: Recepción de la comunicación de IP (días 1-3)...</p>
                        <p className="text-xs text-slate-600 mt-2">Fase 2: Solicitud de informe al SPA (días 4-10)...</p>
                      </div>
                      <div className="pb-4">
                        <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">7. Modelo de carta de continuidad</h4>
                        <p className="text-xs text-slate-600">D./Dña. [NOMBRE], con DNI [XXX], en calidad de [CARGO]...</p>
                      </div>
                      <div className="pb-4">
                        <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider mb-2">8. Checklist de documentación obligatoria</h4>
                        <p className="text-xs text-slate-600">☐ Informe médico de aptitud con limitaciones funcionales...</p>
                      </div>
                    </div>
                    
                    {/* Overlay con marca de agua */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/70 to-white flex items-center justify-center">
                      <div className="text-center">
                        <div className="inline-block border-4 border-slate-300 px-6 py-3 rotate-[-5deg] bg-white/80">
                          <p className="text-2xl font-black text-slate-400 tracking-widest">EJEMPLO</p>
                        </div>
                        <p className="mt-4 text-sm text-slate-500 font-medium">
                          + 4 secciones adicionales en el informe completo
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Indicador de páginas */}
                <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                  <span className="w-2 h-2 rounded-full bg-slate-200"></span>
                  <span className="w-2 h-2 rounded-full bg-slate-200"></span>
                  <span className="ml-2">Página 1 de 3</span>
                </div>
              </div>

              {/* Formulario */}
              <div className="bg-white p-8 shadow-2xl border border-slate-200">
                <h3 className="text-xl font-bold mb-2">Obtenga su informe personalizado</h3>
                <p className="text-slate-500 text-sm mb-6">Complete sus datos para recibir el diagnóstico completo adaptado a su caso.</p>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleLeadSubmit} className="space-y-4 text-left">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Nombre completo *</label>
                    <input 
                      type="text" 
                      placeholder="Su nombre" 
                      required 
                      className="w-full p-4 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none" 
                      value={lead.nombre} 
                      onChange={(e) => setLead({...lead, nombre: e.target.value})}
                      data-testid="lead-nombre"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Email profesional *</label>
                    <input 
                      type="email" 
                      placeholder="correo@empresa.com" 
                      required 
                      className="w-full p-4 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none" 
                      value={lead.email} 
                      onChange={(e) => setLead({...lead, email: e.target.value})}
                      data-testid="lead-email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Empresa</label>
                    <input 
                      type="text" 
                      placeholder="Nombre de su empresa" 
                      className="w-full p-4 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none" 
                      value={lead.empresa} 
                      onChange={(e) => setLead({...lead, empresa: e.target.value})}
                      data-testid="lead-empresa"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">¿Cuál es su principal preocupación?</label>
                    <textarea 
                      placeholder="Describa brevemente su situación..." 
                      className="w-full p-4 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none min-h-[80px] resize-none" 
                      value={lead.preocupacion} 
                      onChange={(e) => setLead({...lead, preocupacion: e.target.value})}
                      data-testid="lead-preocupacion"
                    />
                  </div>
                  <DataProtectionCheckbox
                    checked={privacyAccepted}
                    onChange={setPrivacyAccepted}
                    id="protocolo-privacy-consent"
                  />
                  <button 
                    type="submit" 
                    disabled={loading || !privacyAccepted}
                    className="w-full bg-rose-600 text-white p-5 font-black text-lg hover:bg-rose-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="submit-lead-btn"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={24} />
                    ) : (
                      'Descargar informe completo'
                    )}
                  </button>
                </form>

                <button 
                  onClick={() => setPaso('diagnostico')} 
                  className="mt-6 text-sm text-slate-500 hover:text-slate-900 transition w-full text-center"
                >
                  ← Volver al diagnóstico
                </button>
                
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-[10px] text-slate-400 text-center">
                    Sus datos serán tratados por José Luis Vilaplana Villajos (tecnoabogado.com) conforme a la{' '}
                    <Link to="/legal/privacidad" target="_blank" className="underline hover:text-slate-600">Política de Privacidad</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {paso === 'finalizado' && (
          <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="success-section">
            {/* Banner de Advertencia Legal Crítica */}
            <div className="bg-amber-50 border-l-8 border-amber-500 p-8 flex gap-6 items-start">
              <Scale className="text-amber-600 flex-shrink-0" size={32} />
              <div>
                <h2 className="text-xl font-black text-amber-900 mb-2 uppercase tracking-wide">Aviso legal crítico para la dirección</h2>
                <p className="text-amber-800 leading-relaxed text-sm">
                  La información y el protocolo que acaba de descargar tienen un carácter técnico-preventivo. Su aplicación efectiva para la toma de decisiones extintivas, reubicaciones o alegaciones de "carga excesiva" requiere, por imperativo de la jurisprudencia actual, la supervisión y firma de un <b>abogado laboralista especializado</b>. La implementación por personal no jurídico de estas medidas puede ser motivo de impugnación y declaración de nulidad por discriminación.
                </p>
              </div>
            </div>

            {/* Página de Éxito y CTA */}
            <div className="bg-white p-12 shadow-2xl border border-slate-200 text-center">
              <CheckCircle className="mx-auto text-green-500 mb-6" size={64} />
              <h2 className="text-3xl font-black text-slate-900 mb-4">Su diagnóstico y protocolo están en camino</h2>
              <p className="text-slate-600 mb-2">Recibirá un email en breve con los documentos.</p>
              
              <div className="bg-slate-50 p-8 mt-8 text-left border border-slate-200">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Activity className="text-rose-600" /> El reloj de la Ley 2/2025 ya está en marcha
                </h3>
                <p className="text-slate-600 mb-4">
                  Ha dado el primer paso, pero el marco legal de 2026 no admite esperas. Ante una notificación de incapacidad permanente, su empresa solo dispone de <b>10 días naturales</b> para recibir la comunicación del trabajador y un máximo de <b>3 meses</b> para resolver la adaptación.
                </p>
                <ul className="space-y-2 text-sm text-slate-700 font-medium mb-8">
                  <li className="flex gap-2 items-center"><AlertTriangle size={16} className="text-red-500"/> Nulidad radical del despido por defecto de forma.</li>
                  <li className="flex gap-2 items-center"><AlertTriangle size={16} className="text-red-500"/> Sanciones de hasta 983.736 € por la Inspección.</li>
                  <li className="flex gap-2 items-center"><AlertTriangle size={16} className="text-red-500"/> Responsabilidad penal directa para directivos y técnicos.</li>
                </ul>

                <p className="font-black text-center text-lg text-slate-900 mb-6">No asuma la responsabilidad jurídica en solitario.</p>
                
                <a 
                  href="https://www.tecnoabogado.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full bg-rose-600 text-white p-6 font-black text-xl hover:bg-rose-700 transition-all shadow-xl shadow-rose-200 flex items-center justify-center gap-3 group"
                  data-testid="schedule-session-btn"
                >
                  <PhoneCall className="group-hover:scale-110 transition-transform" />
                  Agendar sesión de estrategia jurídica (15 min)
                </a>
                <p className="text-center text-xs text-slate-400 mt-4">Validaremos la solidez de sus informes ante la doctrina del Tribunal Supremo.</p>
              </div>

              <Link 
                to="/" 
                className="inline-block mt-8 text-sm text-slate-500 hover:text-slate-900 transition"
              >
                ← Volver a la página principal
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Footer simple */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-6 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p>© 2026 Vilaplana Tecnoabogado. Todos los derechos reservados.</p>
          <div className="flex gap-6">
            <a href="https://www.tecnoabogado.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">tecnoabogado.com</a>
            <a href="https://www.vilaplanaabogados.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">vilaplanaabogados.com</a>
            <a href="https://www.despido.net" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">despido.net</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProtocoloPage;
